import {
  combineReducers,
  configureStore,
  createAction,
  createAsyncThunk,
  type PayloadAction,
  type ThunkAction,
  type Middleware,
  type UnknownAction,
} from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import undoable, { ActionCreators, includeAction } from 'redux-undo';

import apiReducer, { askReloadStaleMap } from './modules/api';
import editReducer from './modules/edit';
import { coverSlice } from './modules/cover';
import { imagesSlice } from './modules/images';
import { mapSlice } from './modules/map';
import { modelsSlice } from './modules/models';
import { spacesSlice } from './modules/spaces';
import { markersSlice } from './modules/markers';

import { labelsSlice } from './modules/labels'; // must come after mapSlice, since it depends on it
import { wayfingindSlice } from './modules/wayfinding';
import { pathsSlice } from './modules/paths';

import { saveState, loadState } from './autoSaver';
import { groupedUndo } from './groupedUndo';

import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/pro-solid-svg-icons';
library.add(fas);
//@ts-ignore
export const allIcons = Object.keys(library.definitions.fas);

export type RootState = ReturnType<typeof rootReducer>;

const undoableActions = [
  // Layers
  'images/disable',
  'images/enable',
  'images/rename',
  'images/reorder',
  'images/saveCurrentView',
  // Labels
  'images/addLabelInImage',
  'images/moveLabelInImage',
  'labels/update',
  'images/removeLabelInImage',
  'images/rotateLabelInImage',
  'labels/setLabelStyle',
  'labels/applyStyleToSelectedLabels',
  'labels/mergeStyleIntoSelectedLabels',
  'labels/clearLabelStyle',
  // Spaces
  'images/addSpaceInImage',
  'images/moveSpaceInImage',
  'spaces/update',
  'images/removeSpaceInImage',
  'spaces/reorder',
  // Overlays
  'images/addOverlayInImage',
  'images/updateOverlayInImage',
  'images/removeOverlayInImage',
  // Space Categories
  'spaces/updateCategory',
  'spaces/reorderCategories',

  // Space Markers
  'spaces/setMarker',

  // Space Markers
  'markers/add',
  'markers/update',
  'markers/remove',
  'markers/resize',
  'markers/addDecoration',
  'markers/setDecorations',
  'markers/resize',
];

const rootReducer = combineReducers({
  undoable: undoable(
    combineReducers({
      api: apiReducer,
      images: imagesSlice.reducer,
      labels: labelsSlice.reducer,
      spaces: spacesSlice.reducer,
      markers: markersSlice.reducer,
    }),
    {
      // limit: 40, // limit history size
      filter: includeAction(undoableActions),
      syncFilter: true,
      groupBy: groupedUndo.init([]),
    },
  ),
  cover: coverSlice.reducer,
  edit: editReducer,
  map: mapSlice.reducer,
  models: modelsSlice.reducer,
  wayfinding: wayfingindSlice.reducer,
  paths: pathsSlice.reducer,
});

// Tells other tabs editing the same map that it was saved, so they reload
// before their outdated copy deletes the records saved here
const mapSavesChannel = typeof BroadcastChannel === 'undefined'
  ? null
  : new BroadcastChannel('interactive-map-saves');

// Automatically save to localStorage to recover state between sessions.
const autoSaverMiddleware:Middleware = (storeAPI) => (next) => (action:any) => {

  // Call next, so getState() returns the updated state.
  const result = next(action);

  // Store changes when dispatching undoable actions and after undo/redo
  if (undoableActions.includes(action.type)
      || action.type === '@@redux-undo/UNDO'
      || action.type === '@@redux-undo/REDO') {
    const { undoable, map } = storeAPI.getState();
    saveState(undoable, `${map.order.id}-${map.id}`, map.updatedAt);

  } else if (action.type === 'api/saveAPI/fulfilled') {
    // After saving, delete localStorage
    const { map } = storeAPI.getState();
    saveState(undefined, `${map.order.id}-${map.id}`);
    // Pre-save snapshots may reference records the save deleted
    storeAPI.dispatch(ActionCreators.clearHistory());
    mapSavesChannel?.postMessage({ key: `${map.order.id}-${map.id}`, updatedAt: map.updatedAt });
  }

  return result;
};

// To replace store from localStorage
const replaceStore = createAction<any>('store/replace');
// See https://stackoverflow.com/questions/59424523#73372455
const reducerProxy = (state: any, action: UnknownAction) => {
  if(action.type === 'store/replace') {
    // override undoable with the one provided in payload
    return rootReducer({...state, undoable: action.payload }, action);
  }
  return rootReducer(state, action);
};

const store = configureStore({
  reducer: reducerProxy,
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware().concat(autoSaverMiddleware);
  },
});

mapSavesChannel?.addEventListener('message', ({ data }) => {
  const { map } = store.getState();
  if (
    map.current_user.can_edit
      && data.key === `${map.order.id}-${map.id}`
      && data.updatedAt !== map.updatedAt
  ) {
    askReloadStaleMap();
  }
});

// Infer the `AppDispatch` types from the store itself
export type AppDispatch = typeof store.dispatch;
export type AppThunk<Payload = any, ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  PayloadAction<Payload>
>;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const createAppAsyncThunk = createAsyncThunk.withTypes<{
  state: RootState
  dispatch: AppDispatch
  rejectValue: string
  extra: { s: string; n: number }
}>();

export default store;

export const canUndoSelector = (state: RootState) => state.undoable.past.length > 0;
export const canRedoSelector = (state: RootState) => state.undoable.future.length > 0;

// Create a watcher function that can be called from the store subscribe handler to perform actions when the state changes.
// Can accept a single or an array of selectors and a callback taking the result of that or those, accordingly
type BuildWatcherReturnType = (state: RootState) => void & { force: (state: RootState) => void };
export function buildWatcher<Substate>(
  selector: (state: RootState) => Substate,
  callback: (substate: Substate, previousSubstate: Substate | undefined, state: RootState) => void
): BuildWatcherReturnType;
export function buildWatcher<Substates extends readonly unknown[]>(
  selectors: { [K in keyof Substates]: (state: RootState) => Substates[K] },
  callback: (substates: Substates, previousSubstates: { [K in keyof Substates]: Substates[K] | undefined }, state: RootState) => void
): BuildWatcherReturnType;
export function buildWatcher(
  selectorOrSelectors: ((state: RootState) => unknown) | readonly ((state: RootState) => unknown)[],
  callback: (substateOrSubstates: any, previousSubstateOrSubstates: any, state: RootState) => void
): BuildWatcherReturnType {
  const isArray = Array.isArray(selectorOrSelectors);
  const selectors = isArray ? selectorOrSelectors : [selectorOrSelectors];

  let previousSubstates: unknown[] | undefined = undefined;

  function watcher(state: RootState) {
    const substates = selectors.map((sel) => sel(state));

    if (!substates.some((substate, index) => !Object.is(substate, previousSubstates?.[index]))) return;

    const result = isArray ? substates : substates?.[0];
    const previousResult = isArray ? (previousSubstates ?? []) : (previousSubstates?.[0] ?? []);
    callback(result, previousResult, state);
    previousSubstates = substates;
  }

  watcher.force = function (state: RootState) {
    const substates = selectors.map((sel) => sel(state));
    const result = isArray ? substates : substates?.[0];
    const previousResult = isArray ? (previousSubstates ?? []) : (previousSubstates?.[0] ?? []);
    callback(result, previousResult, state);
    previousSubstates = substates;
  };

  return watcher as unknown as BuildWatcherReturnType;
}

export { saveState, loadState, replaceStore, groupedUndo, store };

export * from './modules/api';
export * from './modules/cover';
export * from './modules/map';
export * from './modules/models';
export * from './modules/images';
export * from './modules/labels';
export * from './modules/spaces';
export * from './modules/edit';
export * from './modules/wayfinding';
export * from './modules/markers';
export * from './modules/paths';
export type * from './types';

export { DEFAULT_PREFERENCES } from './types';
export { DEFAULT_LABEL_STYLE } from './types';
export { DEFAULT_LABEL_GROUP_COLOR } from './types';
